/**
 * Utility functions for handling duration in both ISO 8601 format (PT1H30M) and seconds
 */

/**
 * Parse duration from either ISO 8601 format (PT1H30M) or seconds (3600)
 * @param duration - Duration in ISO 8601 format or seconds
 * @returns Object with hours and minutes
 */
export function parseDuration(duration: string | number | undefined | null): { hours: number; minutes: number } {
  if (!duration) {
    return { hours: 0, minutes: 0 }
  }

  // If it's a number (seconds)
  if (typeof duration === 'number') {
    const hours = Math.floor(duration / 3600)
    const minutes = Math.floor((duration % 3600) / 60)
    return { hours, minutes }
  }

  // If it's a string, check if it's ISO 8601 format
  if (typeof duration === 'string') {
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/)
    if (match) {
      const hours = match[1] ? parseInt(match[1]) : 0
      const minutes = match[2] ? parseInt(match[2]) : 0
      return { hours, minutes }
    }
  }

  return { hours: 0, minutes: 0 }
}

/**
 * Format duration to human-readable string (e.g., "1h 30m" or "30m")
 * @param duration - Duration in ISO 8601 format or seconds
 * @returns Formatted duration string
 */
export function formatDuration(duration: string | number | undefined | null): string {
  const { hours, minutes } = parseDuration(duration)
  
  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`
  } else if (hours > 0) {
    return `${hours}h`
  } else if (minutes > 0) {
    return `${minutes}m`
  }
  
  return ''
}

/**
 * Convert hours and minutes to ISO 8601 duration format
 * @param hours - Number of hours
 * @param minutes - Number of minutes
 * @returns ISO 8601 duration string (e.g., "PT1H30M")
 */
export function toISODuration(hours: number, minutes: number): string {
  return `PT${hours}H${minutes}M`
}

/**
 * Convert hours and minutes to seconds
 * @param hours - Number of hours
 * @param minutes - Number of minutes
 * @returns Duration in seconds
 */
export function toSeconds(hours: number, minutes: number): number {
  return hours * 3600 + minutes * 60
}
